import { PortfolioBusinessType } from "../api/schemas/schemas";

// Translation key (under `modules.properties.businessType`) for each
// PortfolioBusinessType value. Single source of truth used wherever a
// business is rendered with its human label.
export const BUSINESS_TYPE_LABEL_KEY: Record<PortfolioBusinessType, string> = {
  [PortfolioBusinessType.Rooms]: "rooms",
  [PortfolioBusinessType.Renting]: "renting",
  [PortfolioBusinessType.PersonalUse]: "personalUse",
  [PortfolioBusinessType.LocalAccommodation]: "localAccommodation",
  [PortfolioBusinessType.GuestHouse]: "guestHouse",
};

export function getBusinessTypeLabelKey(
  businessType: string | undefined,
): string | undefined {
  if (!businessType) return undefined;
  return BUSINESS_TYPE_LABEL_KEY[businessType as PortfolioBusinessType];
}

// Categorical badge colors per business type (git-label style). Translucent
// tint works on any surface; text shade flips on the dark themes (both `.dark`
// and `.darkest`, which are independent variants).
export const BUSINESS_TYPE_BADGE_CLASS: Record<PortfolioBusinessType, string> = {
  [PortfolioBusinessType.Rooms]:
    "bg-blue-500/10 text-blue-700 border-blue-500/20 dark:text-blue-300 darkest:text-blue-300",
  [PortfolioBusinessType.Renting]:
    "bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:text-emerald-300 darkest:text-emerald-300",
  [PortfolioBusinessType.PersonalUse]:
    "bg-amber-500/10 text-amber-700 border-amber-500/20 dark:text-amber-300 darkest:text-amber-300",
  // Local Accommodation = short-term rental → Airbnb brand color (#FF385C).
  [PortfolioBusinessType.LocalAccommodation]:
    "bg-[#FF385C]/10 text-[#FF385C] border-[#FF385C]/30",
  [PortfolioBusinessType.GuestHouse]:
    "bg-rose-500/10 text-rose-700 border-rose-500/20 dark:text-rose-300 darkest:text-rose-300",
};

// Some house features only make sense for certain business types — e.g. a
// "shared kitchen" only applies when renting out individual rooms, not when
// renting the whole property or a local-accommodation unit. Keys absent from
// this map apply to every business type.
export const HOUSE_FEATURE_BUSINESS_TYPES: Partial<
  Record<string, PortfolioBusinessType[]>
> = {
  sharedKitchen: [PortfolioBusinessType.Rooms],
};

// A house feature is offered when it applies to at least one of the property's
// selected business types (or when it isn't restricted at all).
export function isHouseFeatureAllowed(
  featureKey: string,
  businessTypes: string[],
): boolean {
  const allowed = HOUSE_FEATURE_BUSINESS_TYPES[featureKey];
  if (!allowed) return true;
  return businessTypes.some((bt) =>
    allowed.includes(bt as PortfolioBusinessType),
  );
}

const FALLBACK_BADGE_CLASS = "bg-muted text-muted-foreground border-border";

export function getBusinessTypeBadgeClass(
  businessType: string | undefined,
): string {
  if (!businessType) return FALLBACK_BADGE_CLASS;
  return (
    BUSINESS_TYPE_BADGE_CLASS[businessType as PortfolioBusinessType] ??
    FALLBACK_BADGE_CLASS
  );
}
